import React, { useContext, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { getAnalyticsTableColumns, KPI, RawDataItem, transformData } from 'utils/getTierAnalyticsSimulatorTableColumns';
import { containerId } from './TierAnalyticsChart';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingAnalyticsUrls } from 'config/urls';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { isEmpty } from 'lodash';
import { tierAnalyticsParser } from 'parser/pricingAnalytics/tierAnalyticsParser';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import TierAnalyticsChart from './TierAnalyticsChart';
import { categoryMovementChartParser } from 'parser/pricingAnalytics/categoryMovementParser';
import { CategoryMovementOutputChartItem } from 'types/pricingSimulator';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import Loader from 'components/molecules/Loader';
import { legendData } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';

export interface NumericBooleanMap {
    [key: number]: boolean;
}

export const CustomTableTierAnalytics = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 54px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }

    .ant-table-tbody > tr > td {
        padding: 8px 8px;
        height: 38px;
    }

    border: ${'1px solid #DBDFE9'};
    border-radius: 8px;

    .ant-table th:last-child,
    .ant-table td:last-child {
        min-width: 48px;
        max-width: 48px;
        width: 48px;
    }
`;
const TierAnalyticsContainer = () => {
    const tableRef = useRef(null);
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const {
        categoryMovementKpis,
        tierAnalytics: { leftChartLabels, visualConfig, columnMapping, showNegativesInParenthesis },
    } = pricingSimulatorConfig;
    const [expand, setExpand] = useState<NumericBooleanMap>({});
    const { selectedReportInfo, filters, showPercentageChange } = useContext(PricingAnalyticsDataContext);
    const tierAnalyticsVariables = useMemo(
        () => ({ scenario_id: selectedReportInfo?.baseScenario?.scenarioId, filters: filters }),
        [filters, selectedReportInfo?.baseScenario?.scenarioId],
    );
    const volumeChartVariables = useMemo(
        () => ({
            kpi_list: [categoryMovementKpis[0].value],
            base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
            filters: filters,
            show_percent: showPercentageChange,
        }),
        [filters, selectedReportInfo?.baseScenario?.scenarioId, showPercentageChange],
    );

    const {
        loading: tierAnalyticsLoading,
        error: tierAnalyticsError,
        data,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: !isEmpty(tierAnalyticsVariables) },
        url: pricingAnalyticsUrls.getTierViewData,
        queryKey: ['getTierViewData', tierAnalyticsVariables],
        variables: tierAnalyticsVariables,
        parserFunction: (res: { data: RawDataItem[] }) => {
            return tierAnalyticsParser(res.data, visualConfig);
        },
    });

    const {
        loading: volumeChartLoading,
        error: volumeChartError,
        data: volChartData,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: !isEmpty(volumeChartVariables) },
        url: pricingAnalyticsUrls.getCategoryMovementKpiChartDataUrl,
        queryKey: ['pa-vol-category-movement-chart', volumeChartVariables],
        variables: volumeChartVariables,
        parserFunction: (res: any) => {
            if (res?.error || !res?.length) {
                throw 'No Data';
            }
            const outputObj: CategoryMovementOutputChartItem[] = categoryMovementChartParser(
                [categoryMovementKpis[0]],
                [res[0]],
            );
            return outputObj[0];
        },
    });
    console.log(formatNumWithKMBAlt(-2.0016619265585033e-7, 2, 2));

    const memoisedColumns = useMemo(() => {
        return getAnalyticsTableColumns(
            data?.tableData,
            expand,
            setExpand,
            decimalForNonPercentage,
            decimalForPercentage,
            columnMapping,
            showNegativesInParenthesis,
        );
    }, [data?.tableData, expand]);

    const tableDataSet = useMemo(() => {
        return transformData(data?.tableData, visualConfig);
    }, [data?.tableData]);

    const memoizedExcelData = useMemo(
        () =>
            tableDataSet.map((rowData) => {
                const excelRowData: string[] = [];
                ['Scenario Summary(M)', ...Object.keys(columnMapping)].forEach((kpiKey, colIndex) => {
                    if (colIndex === 0) {
                        excelRowData.push(rowData.label);
                    } else {
                        const breakevenKeys = new Set([
                            'breakeven_volume',
                            'breakeven_volume_variance',
                            'breakeven_percentage',
                        ]);
                        const value = (rowData[kpiKey] as KPI)?.val?.toString() || '';
                        const shouldShow = !breakevenKeys.has(kpiKey) || rowData.showBreakevenMetrics;
                        excelRowData.push(shouldShow ? value : '');
                    }
                });
                return excelRowData;
            }),
        [tableDataSet],
    );

    const excelHeaders = useMemo(() => ['Scenario Summary(M)', ...Object.values(columnMapping)], [columnMapping]);
    return (
        <div>
            <span className="flex items-center gap-2 justify-end">
                <CopyToClipBoard refOfComponent={tableRef} />
                <DownloadExcel
                    title={'Tier View Table Data'}
                    excelData={memoizedExcelData}
                    headers={excelHeaders}
                    excludeFilters
                />
            </span>
            <CustomTableTierAnalytics
                ref={tableRef}
                className="mb-5 mt-2"
                bordered
                dataSource={tableDataSet}
                columns={memoisedColumns}
                pagination={false}
                scroll={{ x: '100%', y: '318px' }}
                sticky
                loading={tierAnalyticsLoading}
            />
            <div id={containerId} className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-backgroundGreyAlt1 rounded-lg">
                    {tierAnalyticsLoading ? (
                        <div style={{ height }}>
                            <Loader />
                        </div>
                    ) : tierAnalyticsError ? (
                        <div style={{ height }}>
                            <ErrorBox msg={String(tierAnalyticsError)} />
                        </div>
                    ) : (
                        <TierAnalyticsChart
                            chartConfig={data?.chartConfig}
                            labelObject={leftChartLabels}
                            decimalForNonPercentage={decimalForNonPercentage}
                            decimalForPercentage={decimalForPercentage}
                            chartContainer="tier-analytics-left"
                        />
                    )}
                </div>
                <div className="p-4 border border-backgroundGreyAlt1 rounded-lg">
                    {volumeChartLoading ? (
                        <div style={{ height }}>
                            <Loader />
                        </div>
                    ) : volumeChartError ? (
                        <div style={{ height }}>
                            <ErrorBox msg={String(tierAnalyticsError)} />
                        </div>
                    ) : (
                        <TierAnalyticsChart
                            chartConfig={{
                                chartData: volChartData?.data ?? [],
                                legendData,
                                maxVal: volChartData?.maxVal,
                                minVal: volChartData?.minVal,
                                suffix: showPercentageChange ? '%' : '',
                            }}
                            labelObject={volChartData?.labelObject}
                            decimalForNonPercentage={decimalForNonPercentage}
                            decimalForPercentage={decimalForPercentage}
                            chartContainer="tier-analytics-right"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TierAnalyticsContainer;
